import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useWindowSize, useUserPreferences } from '../../../hooks';
import { formatProfessionalNameForHeader } from '../../../utils/formatUtils';

/**
 * Barra de ferramentas personalizada para o calendário
 * Design inspirado no Instagram e X (Twitter)
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Date} props.date - Data atual do calendário
 * @param {Function} props.onNavigate - Função para navegar no calendário
 * @param {Function} props.onView - Função para mudar a visualização
 * @param {Object} props.viewLabels - Rótulos para as visualizações disponíveis
 * @param {Object} props.views - Visualizações disponíveis
 * @param {string} props.view - Visualização atual (do React Big Calendar)
 * @param {string} props.currentView - Visualização atual (compatibilidade)
 * @param {Function} props.onColumnAdjust - Função para ajustar o número de colunas
 * @param {Function} props.onOpenBlockModal - Função para abrir o modal de bloqueio
 * @param {boolean} props.isMobile - Flag indicando se é dispositivo móvel
 * @returns {JSX.Element} - Elemento React
 */
const TimelineToolbar = ({ 
  date, 
  onNavigate, 
  onView, 
  viewLabels = {},
  views = {}, 
  view,
  currentView,
  onColumnAdjust,
  onOpenBlockModal,
  isMobile
}) => {
  // Usar view ou currentView, com fallback para "day"
  const activeView = view || currentView || "day";
  
  // Detectar o tamanho da janela para caso a prop isMobile não seja fornecida
  const { width } = useWindowSize();
  // Usar o valor da prop se fornecido, caso contrário detectar com base no tamanho da tela
  const isDeviceMobile = isMobile !== undefined ? isMobile : width < 768;
  
  // Usar o hook de preferências do usuário
  const { preferences, updatePreference } = useUserPreferences();
  
  // Estado para controlar o menu de opções avançadas
  const [showAdvancedMenu, setShowAdvancedMenu] = useState(false);
  const advancedMenuRef = useRef(null);
  
  // Estado para controlar a exibição do calendário
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  
  // Estado para controlar o mês visualizado no calendário
  const [calendarViewDate, setCalendarViewDate] = useState(date);
  
  // Atualizar a data de visualização do calendário quando a data principal mudar
  useEffect(() => {
    setCalendarViewDate(date);
  }, [date]);
  
  // Fechar os menus quando clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (advancedMenuRef.current && !advancedMenuRef.current.contains(event.target)) {
        setShowAdvancedMenu(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [advancedMenuRef, calendarRef]);
  
  // Manipulador para troca de visualização
  const handleViewChange = (newView) => {
    if (typeof onView === 'function') {
      onView(newView);
      setShowAdvancedMenu(false);
    }
  };
  
  // Manipulador seguro para navegação
  const handleNavigate = (action, newDate) => {
    if (typeof onNavigate === 'function') {
      onNavigate(action, newDate);
    }
  };
  
  // Formatar a data para exibição
  const formattedDay = date instanceof Date ? format(date, "d", { locale: ptBR }) : '';
  const formattedMonth = date instanceof Date ? format(date, "MMM", { locale: ptBR }) : '';
  const formattedYear = date instanceof Date ? format(date, "yyyy", { locale: ptBR }) : '';
  
  // Formato completo para exibição em tooltips
  const fullFormattedDate = date instanceof Date ? format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR }) : '';
  
  // Formatação para input type="date" (YYYY-MM-DD)
  const dateForInput = date instanceof Date ? format(date, "yyyy-MM-dd") : '';
  
  // Verificar se a visualização inclui recursos (profissionais)
  const isResourceView = activeView?.toLowerCase().includes('resource');
  
  // Função para ajustar o número de colunas e salvar na preferência do usuário
  const handleColumnChange = (count) => {
    if (typeof onColumnAdjust === 'function') {
      onColumnAdjust(count);
      updatePreference('mobileColumns', count);
      setShowAdvancedMenu(false);
    }
  };
  
  // Valor atual de colunas a partir das preferências do usuário
  const currentMobileColumns = preferences.mobileColumns || 2;
  
  // Função para selecionar uma data específica
  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      handleNavigate('DATE', selectedDate);
      setShowCalendar(false);
    }
  };
  
  // Handler para quando o usuário seleciona uma data no calendário nativo do sistema
  const handleNativeDateChange = (e) => {
    if (e.target.value) {
      const [year, month, day] = e.target.value.split('-').map(Number);
      const newDate = new Date(year, month - 1, day);
      
      // Usar a mesma função de navegação para manter consistência
      handleNavigate('DATE', newDate);
    }
  };
  
  // Handler seguro para abrir o seletor de data nativo
  const openNativePicker = () => {
    const datePicker = document.getElementById('nativeDatePicker');
    
    if (!datePicker) return;
    
    // Em dispositivos iOS, o método click() funciona melhor que showPicker()
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      datePicker.focus();
      datePicker.click();
      return;
    }
    
    // Verificar se o método showPicker está disponível (navegadores modernos)
    if (typeof datePicker.showPicker === 'function') {
      try {
        datePicker.showPicker();
      } catch (error) {
        // Fallback: usar método click() para navegadores que não suportam showPicker
        datePicker.focus();
        datePicker.click();
      }
    } else {
      // Fallback para navegadores que não suportam showPicker
      datePicker.focus();
      datePicker.click();
    }
  };
  
  // Função para navegar entre meses no calendário popup sem fechá-lo
  const handleCalendarMonthNavigation = (direction) => {
    const newMonth = new Date(calendarViewDate);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else if (direction === 'next') {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCalendarViewDate(newMonth);
  };
  
  // Função para gerar o calendário
  const renderCalendar = () => {
    if (!calendarViewDate || !(calendarViewDate instanceof Date)) return null;
    
    // Obter o primeiro dia do mês
    const year = calendarViewDate.getFullYear();
    const month = calendarViewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    
    // Obter o número de dias no mês
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Obter o dia da semana do primeiro dia (0 = domingo, 1 = segunda, etc.)
    const firstDayOfWeek = firstDay.getDay();
    
    // Nomes dos dias da semana
    const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    // Nome do mês
    const monthName = format(calendarViewDate, 'MMMM yyyy', { locale: ptBR });
    
    // Data atual para comparação (hoje)
    const today = new Date();
    const isToday = (day) => 
      day.getDate() === today.getDate() && 
      day.getMonth() === today.getMonth() && 
      day.getFullYear() === today.getFullYear();
    
    // Verificar se um dia é o dia selecionado
    const isSelectedDay = (day) => 
      day.getDate() === date.getDate() && 
      day.getMonth() === date.getMonth() && 
      day.getFullYear() === date.getFullYear();
    
    // Criar array de semanas
    const weeks = [];
    let days = [];
    
    // Adicionar dias vazios antes do primeiro dia do mês
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Adicionar dias do mês
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
      
      // Se completou uma semana ou chegou ao último dia, adicionar à lista de semanas
      if (days.length === 7 || i === daysInMonth) {
        weeks.push([...days]);
        days = [];
      }
    }
    
    // Preencher a última semana com dias vazios
    while (days.length > 0 && days.length < 7) {
      days.push(null);
      weeks.push([...days]);
      days = [];
    }
    
    return (
      <div className="calendar-popup bg-white rounded-lg shadow-md border border-gray-200 p-2">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-medium text-center capitalize">{monthName}</h3>
          <div className="flex space-x-1">
            <button 
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => handleCalendarMonthNavigation('prev')}
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => handleCalendarMonthNavigation('next')}
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="calendar-grid">
          {/* Cabeçalho dos dias da semana */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdays.map((day, index) => (
              <div key={index} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>
          
          {/* Grid de dias */}
          <div className="calendar-weeks">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-7 gap-1 mb-1">
                {week.map((day, dayIndex) => day ? (
                  <button
                    key={dayIndex}
                    className={`w-7 h-7 text-xs flex items-center justify-center rounded-full 
                      ${isSelectedDay(day) ? 'bg-blue-500 text-white' : 
                         isToday(day) ? 'bg-blue-100/70 text-blue-600 font-medium' : 
                         'hover:bg-gray-100'}`}
                    onClick={() => handleDateSelect(day)}
                    onTouchStart={handleTouchEvent}
                    onTouchEnd={handleTouchEvent}
                  >
                    {day.getDate()}
                  </button>
                ) : (
                  <div key={dayIndex} className="w-7 h-7"></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  // Renderizar calendário customizado (desktop) ou input nativo (mobile)
  const renderDateSelector = () => {
    if (isDeviceMobile) {
      // No mobile, usamos um input date nativo oculto e um botão que o aciona
      return (
        <div 
          className="flex items-center relative"
          ref={calendarRef}
        >
          {/* Wrapper para aumentar a área de toque */}
          <div 
            className="relative flex-1 cursor-pointer"
            onClick={openNativePicker}
            style={{ padding: '4px 0' }}
          >
            <input 
              type="date" 
              id="nativeDatePicker"
              value={dateForInput}
              onChange={handleNativeDateChange}
              className="absolute opacity-0 cursor-pointer"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                zIndex: -1, // Ficar atrás do botão visível
                touchAction: 'manipulation',
                // Necessário para iOS
                WebkitAppearance: 'none'
              }}
              aria-label="Selecionar data"
            />
            <button
              className="flex items-center text-base font-medium text-gray-800 whitespace-nowrap hover:bg-gray-50 px-3 py-2 rounded transition-colors touch-manipulation w-full"
              onClick={openNativePicker}
              onTouchStart={(e) => {
                handleTouchEvent(e);
                // Forçar o clique no input quando o botão é tocado para garantir compatibilidade
                setTimeout(() => {
                  const datePicker = document.getElementById('nativeDatePicker');
                  if (datePicker) {
                    datePicker.click();
                  }
                }, 10);
              }}
              onTouchEnd={handleTouchEvent}
              title={fullFormattedDate}
              style={{ minHeight: '40px', touchAction: 'manipulation' }}
            >
              <span className="font-bold mr-1">{formattedDay}</span>
              <span className="lowercase mr-1">{formattedMonth}</span>
              <span>{formattedYear}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      );
    } else {
      // No desktop, mantemos o comportamento original com o calendário personalizado
      return (
        <div className="flex items-center relative" ref={calendarRef}>
          <button
            className="flex items-center text-base font-medium text-gray-800 whitespace-nowrap hover:bg-gray-50 px-2 py-1 rounded transition-colors"
            onClick={() => setShowCalendar(!showCalendar)}
            onTouchStart={handleTouchEvent}
            onTouchEnd={handleTouchEvent}
            title={fullFormattedDate}
          >
            <span className="font-bold mr-1">{formattedDay}</span>
            <span className="lowercase mr-1">{formattedMonth}</span>
            <span>{formattedYear}</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
          
          {showCalendar && (
            <div className="absolute top-full left-0 mt-1 z-50">
              {renderCalendar()}
            </div>
          )}
        </div>
      );
    }
  };
  
  // Função para manipular eventos de toque e evitar propagação
  const handleTouchEvent = (e) => {
    e.stopPropagation();
  };
  
  return (
    <div className="flex justify-between items-center p-2 bg-white border-b relative">
      {/* Layout de três colunas para centralizar o calendário */}
      <div className="grid grid-cols-3 w-full">
        {/* Coluna esquerda - vazia no mobile, exceto pelo botão de hoje em desktop */}
        <div className="flex items-center justify-start">
          {!isDeviceMobile && (
            <button
              onClick={() => handleNavigate('TODAY')}
              className="text-sm md:text-base font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-md"
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
            >
              Hoje
            </button>
          )}
        </div>
        
        {/* Coluna central - grupo de navegação de data centralizado */}
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handleNavigate('PREV')}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Seleção de data */}
            {renderDateSelector()}
            
            <button
              onClick={() => handleNavigate('NEXT')}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Coluna direita - botão de configurações */}
        <div className="flex items-center justify-end">
          <div ref={advancedMenuRef} className="relative">
            <button
              onClick={() => setShowAdvancedMenu(!showAdvancedMenu)}
              onTouchStart={handleTouchEvent}
              onTouchEnd={handleTouchEvent}
              className="px-2 py-1 bg-white hover:bg-gray-100 text-gray-700 border rounded-md flex items-center"
              aria-label="Configurações"
              title="Configurações"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Menu dropdown de opções avançadas */}
            {showAdvancedMenu && (
              <div className="absolute right-0 mt-1 py-1 w-48 bg-white rounded-md shadow-lg z-20 border text-xs">
                {/* Opção de bloquear horário */}
                <button 
                  type="button" 
                  className="w-full px-3 py-1 text-left hover:bg-gray-100 flex items-center"
                  onClick={() => {
                    onOpenBlockModal();
                    setShowAdvancedMenu(false);
                  }}
                  onTouchStart={handleTouchEvent}
                  onTouchEnd={handleTouchEvent}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Bloquear Horário
                </button>
                
                {/* Opção de ajustar número de colunas - Mostrada sempre no mobile */}
                {isDeviceMobile && (
                  <div className="px-3 py-2 border-t">
                    <p className="text-xxs text-gray-500 mb-1">Colunas visíveis:</p>
                    <div className="flex justify-between gap-1 mt-1">
                      {[1, 2, 3, 4].map(count => (
                        <button 
                          key={count}
                          className={`w-8 h-8 rounded flex items-center justify-center text-sm 
                                    ${count === currentMobileColumns ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          onClick={() => handleColumnChange(count)}
                          onTouchStart={handleTouchEvent}
                          onTouchEnd={handleTouchEvent}
                          aria-label={`${count} colunas`}
                          title={`Exibir ${count} colunas`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Opção de ajustar número de colunas para desktop - Só mostrada se estiver em uma visão de recursos */}
                {!isDeviceMobile && isResourceView && (
                  <div className="px-3 py-2 border-t">
                    <p className="text-xxs text-gray-500 mb-1">Colunas visíveis:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {[3, 5, 8, 10].map(count => (
                        <button 
                          key={count}
                          className={`w-7 h-7 rounded flex items-center justify-center text-xs 
                                    ${count === 3 ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          onClick={() => {
                            if (typeof onColumnAdjust === 'function') {
                              onColumnAdjust(count);
                              setShowAdvancedMenu(false);
                            }
                          }}
                          onTouchStart={handleTouchEvent}
                          onTouchEnd={handleTouchEvent}
                          aria-label={`${count} colunas`}
                          title={`Exibir ${count} colunas`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineToolbar; 